import React, { useMemo, useState } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import { apiRequest } from '../services/apiClient';
import { PRODUCT_NAME } from '../config/appBranding';
import './SetupWizard.css';

// Same key LocationSetupPage.js reads and DirectorLocationsPanel.js
// writes — pre-selects a location when navigating to /location-setup.
const LOCATION_SETUP_STORAGE_KEY = 'manager-ops-location-setup-id';

const SETUP_STEPS = [
  {
    key: 'company',
    title: 'Company Setup',
    detail: 'Your private workspace is ready under the authenticated company profile.'
  },
  {
    key: 'locations',
    title: 'Add Locations',
    detail: 'Create the locations that leaders and goals will be assigned to.',
    route: '/locations',
    countKey: 'locations'
  },
  {
    key: 'team',
    title: 'Add Leaders & Team',
    detail: 'Add leaders and team members so dashboards have clear ownership.',
    route: '/managers',
    countKey: 'managers'
  },
  {
    key: 'kpis',
    title: 'Choose KPI Template',
    detail: 'Starter Mode uses the default KPI set first; deep KPI admin unlocks after launch.',
    countKey: 'kpis'
  },
  {
    key: 'goals',
    title: 'Set First Goals',
    detail: 'Create initial location goals so the Executive Snapshot has signal on day one.',
    countKey: 'goals'
  },
  {
    key: 'launch',
    title: 'Launch Executive Snapshot',
    detail: 'Open the full leadership workspace once the essentials are in place.',
    route: '/dashboard-preview'
  }
];

function getCompletedStepSet(status) {
  return new Set(Array.isArray(status?.completedSteps) ? status.completedSteps : []);
}

function SetupWizard({ onboardingStatus, onNavigate, onStatusChange, user, accessibleLocations = [] }) {
  const [activeStep, setActiveStep] = useState(onboardingStatus?.currentStep || 'company');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [kpiPreview, setKpiPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const [kpiTemplateError, setKpiTemplateError] = useState('');
  const [kpiTemplateMessage, setKpiTemplateMessage] = useState('');
  const [selectedGoalLocationId, setSelectedGoalLocationId] = useState('');
  const counts = onboardingStatus?.counts || {};
  const activeLocations = useMemo(
    () => (Array.isArray(accessibleLocations) ? accessibleLocations.filter(location => location?.isActive) : []),
    [accessibleLocations]
  );
  const completedStepSet = useMemo(() => getCompletedStepSet(onboardingStatus), [onboardingStatus]);
  const completedCount = SETUP_STEPS.filter(step => completedStepSet.has(step.key)).length;
  const progressPercent = Math.round((completedCount / SETUP_STEPS.length) * 100);
  const canLaunch = Boolean(onboardingStatus?.isComplete || (
    counts.locations > 0
    && counts.managers > 0
    && counts.users > 0
    && counts.kpis > 0
    && counts.goals > 0
  ));

  const saveProgress = async (nextStepKey) => {
    setActiveStep(nextStepKey);
    setMessage('');
    setError('');

    try {
      setIsSaving(true);
      const nextStatus = await apiRequest('/api/onboarding/progress', {
        method: 'PATCH',
        body: JSON.stringify({
          currentStep: nextStepKey,
          completedSteps: Array.from(completedStepSet)
        })
      });
      onStatusChange(nextStatus);
      setMessage('Progress saved.');
    } catch (saveError) {
      setError(saveError.message || 'Unable to save setup progress.');
    } finally {
      setIsSaving(false);
    }
  };

  const launchWorkspace = async () => {
    setMessage('');
    setError('');

    try {
      setIsSaving(true);
      const nextStatus = await apiRequest('/api/onboarding/complete', { method: 'POST' });
      onStatusChange(nextStatus);
      onNavigate('/executive');
    } catch (launchError) {
      setError(launchError.message || 'Unable to launch the workspace yet.');
    } finally {
      setIsSaving(false);
    }
  };

  const loadKpiTemplatePreview = async () => {
    setKpiTemplateError('');
    setKpiTemplateMessage('');
    setIsPreviewLoading(true);
    try {
      const preview = await apiRequest('/api/kpis/starter-template/preview');
      setKpiPreview(preview);
    } catch (previewError) {
      setKpiTemplateError(previewError.message || 'Unable to load the Starter KPI Template preview.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const applyStarterKpiTemplate = async () => {
    setKpiTemplateError('');
    setKpiTemplateMessage('');
    setIsApplyingTemplate(true);
    try {
      const result = await apiRequest('/api/kpis/starter-template/apply', { method: 'POST' });
      const nextStatus = await apiRequest('/api/onboarding/status');
      onStatusChange(nextStatus);
      setKpiPreview(null);
      setKpiTemplateMessage(
        result.created.length
          ? `Applied Starter KPI Template — ${result.created.length} KPI${result.created.length === 1 ? '' : 's'} created${result.alreadyPresent.length ? `, ${result.alreadyPresent.length} already present` : ''}.`
          : 'Starter KPIs are already present — nothing new to add.'
      );
    } catch (applyError) {
      setKpiTemplateError(applyError.message || 'Unable to apply the Starter KPI Template. You can retry.');
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  const dismissKpiPreview = () => {
    setKpiPreview(null);
    setKpiTemplateError('');
  };

  // Opens the existing per-location goal setup (LocationSetupPage,
  // which already has manual goal creation AND the "Use Company KPI
  // Template" baseline-goal path) pre-selected to locationId, the
  // same handoff DirectorLocationsPanel already uses. Step 5 never
  // builds its own goal-creation UI — it only ever offers locationIds
  // drawn from `accessibleLocations`, which is already tenant-scoped
  // by the time it reaches this component (see App.js), so a
  // cross-tenant id can never appear as a choice here.
  const openLocationGoalSetup = (locationId) => {
    if (!locationId) {
      return;
    }
    try {
      window.sessionStorage.setItem(LOCATION_SETUP_STORAGE_KEY, String(locationId));
    } catch {
      // Keep the wizard usable when storage is unavailable.
    }
    onNavigate('/location-setup');
  };

  // Sprint 3B.7: SetupWizard migrated to WorkspacePage with
  // width="operational". The inline .setup-hero block is removed —
  // its eyebrow / title / subtitle flow into WorkspacePage's standard
  // slots, and the progress card moves into the contextSlot so it
  // stays visually paired with the workspace header. The inner
  // .setup-wizard wrapper survives so the existing .setup-grid layout
  // (steps panel + active-step detail) keeps applying.
  const headerContext = (
    <div className="setup-progress-card" aria-label="Setup progress">
      <span>{progressPercent}%</span>
      <strong>{completedCount} of {SETUP_STEPS.length}</strong>
      <div className="setup-progress-track">
        <div className="setup-progress-fill" style={{ width: `${progressPercent}%` }}></div>
      </div>
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="setup-wizard"
      width="operational"
      eyebrow="Starter Mode"
      title={`Get ${user?.companyName || 'your company'} operational in minutes`}
      subtitle="Essential setup is front and center. Advanced governance, policy, import/export, audit, and deep KPI tools unlock after launch."
      contextSlot={headerContext}
    >
      <section className="setup-wizard" aria-labelledby="setup-wizard-title">
      <div className="setup-grid">
        <div className="setup-steps-panel">
          {SETUP_STEPS.map((step, index) => {
            const isComplete = completedStepSet.has(step.key);
            const isActive = activeStep === step.key;
            const count = step.countKey ? Number(counts[step.countKey] || 0) : null;

            return (
              <button
                key={step.key}
                type="button"
                className={`setup-step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`}
                onClick={() => saveProgress(step.key)}
              >
                <span className="setup-step-index">{isComplete ? <i className="fas fa-check"></i> : index + 1}</span>
                <span className="setup-step-body">
                  <strong>{step.title}</strong>
                  <small>{step.detail}</small>
                  {count !== null ? <em>{count} ready</em> : null}
                </span>
              </button>
            );
          })}
        </div>

        <div className="setup-action-panel">
          {activeStep === 'kpis' ? (
            <div className="setup-kpi-template" aria-labelledby="kpi-template-heading">
              <div className="setup-action-header">
                <span>Step 4</span>
                <strong id="kpi-template-heading">Starter KPI Template</strong>
              </div>

              <p>
                {counts.kpis > 0
                  ? `${counts.kpis} KPI${counts.kpis === 1 ? '' : 's'} already ready for ${user?.companyName || 'this company'}. Continue with them, or preview and add any missing Starter KPIs below.`
                  : 'No KPIs yet. Preview the Starter KPI Template before applying it.'}
              </p>

              {isPreviewLoading ? <div className="setup-message" role="status">Loading Starter KPI Template preview…</div> : null}

              {kpiPreview ? (
                <div className="setup-kpi-preview-list">
                  {kpiPreview.items.map(item => (
                    <div className="setup-kpi-preview-item" key={item.kpiKey}>
                      <div>
                        <strong>{item.label}</strong>
                        <small>{item.description}</small>
                      </div>
                      <span className={item.alreadyPresent ? 'kpi-status present' : 'kpi-status pending'}>
                        {item.alreadyPresent ? 'Already present' : 'Will be created'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {kpiTemplateMessage ? <div className="setup-message success" role="status">{kpiTemplateMessage}</div> : null}
              {kpiTemplateError ? (
                <div className="setup-message error" role="alert">
                  {kpiTemplateError}
                </div>
              ) : null}

              <div className="setup-actions setup-kpi-actions">
                {!kpiPreview ? (
                  <button
                    type="button"
                    onClick={loadKpiTemplatePreview}
                    disabled={isPreviewLoading || isApplyingTemplate}
                  >
                    <i className="fas fa-eye"></i>
                    {kpiTemplateError ? 'Retry Preview' : 'Preview Starter KPI Template'}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={applyStarterKpiTemplate}
                      disabled={isApplyingTemplate || kpiPreview.items.every(item => item.alreadyPresent)}
                    >
                      <i className="fas fa-check"></i>
                      {isApplyingTemplate
                        ? 'Applying…'
                        : (kpiTemplateError ? 'Retry Apply' : 'Apply Starter KPI Template')}
                    </button>
                    <button type="button" onClick={dismissKpiPreview} disabled={isApplyingTemplate}>
                      Cancel
                    </button>
                  </>
                )}
                {counts.kpis > 0 ? (
                  <button type="button" onClick={() => saveProgress('goals')} disabled={isSaving}>
                    Continue with Existing KPIs
                  </button>
                ) : null}
              </div>
            </div>
          ) : activeStep === 'goals' ? (
            <div className="setup-goal-template" aria-labelledby="goal-setup-heading">
              <div className="setup-action-header">
                <span>Step 5</span>
                <strong id="goal-setup-heading">Set Initial Goals</strong>
              </div>

              {counts.kpis === 0 ? (
                <p>
                  Add at least one KPI in Step 4 before setting initial goals — goals are always attached to a KPI.
                </p>
              ) : activeLocations.length === 0 ? (
                <p>Add an active location in Step 2 before setting initial goals.</p>
              ) : (
                <>
                  <p>
                    {counts.goals > 0
                      ? `${counts.goals} goal${counts.goals === 1 ? '' : 's'} ready.`
                      : (activeLocations.length === 1
                        ? `Set initial goals for ${activeLocations[0].code} - ${activeLocations[0].name}. This opens the existing location goal setup, where you can use the Company KPI Template to generate goals for all your KPIs at once, or add them one at a time.`
                        : 'Choose a location. Initial goals must be established for each applicable location.')}
                  </p>

                  {activeLocations.length > 1 ? (
                    <select
                      aria-label="Choose location for initial goals"
                      value={selectedGoalLocationId}
                      onChange={(event) => setSelectedGoalLocationId(event.target.value)}
                    >
                      <option value="">Choose a location</option>
                      {activeLocations.map(location => (
                        <option key={location.id} value={location.id}>{location.code} - {location.name}</option>
                      ))}
                    </select>
                  ) : null}

                  <div className="setup-actions setup-goal-actions">
                    <button
                      type="button"
                      onClick={() => openLocationGoalSetup(
                        activeLocations.length === 1 ? activeLocations[0].id : selectedGoalLocationId
                      )}
                      disabled={activeLocations.length > 1 && !selectedGoalLocationId}
                    >
                      <i className="fas fa-bullseye"></i>
                      Set Initial Goals
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="setup-action-header">
                <span>{PRODUCT_NAME}</span>
                <strong>{user?.companyName || PRODUCT_NAME}</strong>
              </div>
              <div className="setup-metric-row">
                <div><strong>{counts.locations || 0}</strong><span>Locations</span></div>
                <div><strong>{counts.managers || 0}</strong><span>Team</span></div>
                <div><strong>{counts.kpis || 0}</strong><span>KPIs</span></div>
                <div><strong>{counts.goals || 0}</strong><span>Goals</span></div>
              </div>

              <div className="setup-actions">
                <button type="button" onClick={() => onNavigate('/locations')}>
                  <i className="fas fa-location-dot"></i>
                  Locations
                </button>
                <button type="button" onClick={() => onNavigate('/managers')}>
                  <i className="fas fa-users"></i>
                  Team
                </button>
                <button type="button" onClick={() => onNavigate('/dashboard-preview')}>
                  <i className="fas fa-chart-simple"></i>
                  Preview
                </button>
              </div>

              <div className="setup-launch-card">
                <span>Advanced settings are skipped for now.</span>
                <p>Governance, approvals complexity, policy admin, imports/exports, audit tools, and deep KPI admin stay out of the way until onboarding is complete.</p>
                <button type="button" onClick={launchWorkspace} disabled={!canLaunch || isSaving}>
                  <i className="fas fa-rocket"></i>
                  Launch Executive Snapshot
                </button>
              </div>
            </>
          )}

          {message ? <div className="setup-message success">{message}</div> : null}
          {error ? <div className="setup-message error">{error}</div> : null}
        </div>
      </div>
      </section>
    </WorkspacePage>
  );
}

export default SetupWizard;
